import { useState, useCallback, useRef } from "react";

export const SearchStages = {
  GENERATING_QUERIES: "generating_queries",
  SEARCHING_PAPERS: "searching_papers",
  FETCHING_DETAILS: "fetching_details",
  EXTRACTING_STRUCTURED_FIELDS: "extracting_structured_fields",
  EXTRACTING_IMAGES: "extracting_images",
  ANALYZING_RESULTS: "analyzing_results",
  OVERVIEW_STREAMING: "overview_streaming",
  OVERVIEW_COMPLETE: "overview_complete",
  COMPLETED: "completed",
  FAILED: "failed",
};

const PROGRESS_TRACKED_STAGES = [
  SearchStages.GENERATING_QUERIES,
  SearchStages.SEARCHING_PAPERS,
  SearchStages.FETCHING_DETAILS,
  SearchStages.ANALYZING_RESULTS,
];

const FINAL_PROGRESS_STAGE = SearchStages.ANALYZING_RESULTS;

const createTrackedStagesState = () =>
  PROGRESS_TRACKED_STAGES.reduce((acc, stageName) => {
    acc[stageName] = createInitialStageState();
    return acc;
  }, {});

// Define initial stage state
const createInitialStageState = () => ({
  status: "pending", // 'pending' | 'in_progress' | 'completed' | 'failed'
  progress: null, // { current: number, total: number, percentage: number }
  data: null, // Stage-specific data
  error: null,
  startTime: null,
  endTime: null,
});

// Define initial progress state
const createInitialProgressState = () => ({
  searchId: null,
  stages: createTrackedStagesState(),
  currentStage: null,
  overallProgress: 0,
  startTime: null,
  endTime: null,
  isSearching: false,
});

export const useSearchProgress = () => {
  const [progress, setProgress] = useState(createInitialProgressState());
  const progressRef = useRef(progress);
  progressRef.current = progress;

  // Reset progress to initial state
  const resetProgress = useCallback(() => {
    setProgress(createInitialProgressState());
  }, []);

  const startStage = useCallback((stage, searchId = null) => {
    if (!stage || !PROGRESS_TRACKED_STAGES.includes(stage)) {
      return;
    }

    setProgress((prev) => {
      const now = new Date();
      const newProgress = { ...prev };

      if (!newProgress.searchId && searchId) {
        newProgress.searchId = searchId;
      }
      if (!newProgress.startTime) {
        newProgress.startTime = now;
      }

      if (
        newProgress.currentStage &&
        newProgress.currentStage !== stage &&
        newProgress.stages[newProgress.currentStage] &&
        newProgress.stages[newProgress.currentStage].status === "in_progress"
      ) {
        newProgress.stages[newProgress.currentStage] = {
          ...newProgress.stages[newProgress.currentStage],
          status: "completed",
          endTime: now,
        };
      }

      newProgress.currentStage = stage;
      newProgress.isSearching = true;
      newProgress.stages[stage] = {
        ...newProgress.stages[stage],
        status: "in_progress",
        startTime: newProgress.stages[stage].startTime || now,
      };

      const stageCount = Object.keys(newProgress.stages).length;
      const completedCount = Object.values(newProgress.stages).filter(
        (s) => s.status === "completed",
      ).length;
      newProgress.overallProgress = (completedCount / stageCount) * 100;

      return newProgress;
    });
  }, []);

  const startStageWithCompletedHistory = useCallback(
    ({ stage, searchId = null, completedStages = [] }) => {
      if (!stage || !PROGRESS_TRACKED_STAGES.includes(stage)) {
        return;
      }

      setProgress((prev) => {
        const now = new Date();
        const newProgress = { ...prev };

        if (!newProgress.searchId && searchId) {
          newProgress.searchId = searchId;
        }
        if (!newProgress.startTime) {
          newProgress.startTime = now;
        }

        for (const completedStage of completedStages) {
          if (!PROGRESS_TRACKED_STAGES.includes(completedStage)) {
            continue;
          }
          newProgress.stages[completedStage] = {
            ...newProgress.stages[completedStage],
            status: "completed",
            startTime: newProgress.stages[completedStage].startTime || now,
            endTime: newProgress.stages[completedStage].endTime || now,
          };
        }

        newProgress.currentStage = stage;
        newProgress.isSearching = true;
        newProgress.stages[stage] = {
          ...newProgress.stages[stage],
          status: "in_progress",
          startTime: newProgress.stages[stage].startTime || now,
        };

        const stageCount = Object.keys(newProgress.stages).length;
        const completedCount = Object.values(newProgress.stages).filter(
          (s) => s.status === "completed",
        ).length;
        newProgress.overallProgress = (completedCount / stageCount) * 100;

        return newProgress;
      });
    },
    [],
  );

  // Handle search status update
  const handleStatusUpdate = useCallback((data) => {
    setProgress((prev) => {
      const newProgress = { ...prev };

      const nextSearchId = data.run_id || data.search_id;
      if (nextSearchId && !newProgress.searchId) {
        newProgress.searchId = nextSearchId;
        newProgress.startTime = new Date();
      }

      if (data.status === "running") {
        newProgress.isSearching = true;
      }

      if (data.status === "criteria_confirmation_required") {
        newProgress.isSearching = false;
      }

      if (data.status === SearchStages.COMPLETED) {
        newProgress.isSearching = false;
        newProgress.endTime = new Date();
        newProgress.currentStage = SearchStages.COMPLETED;
        Object.keys(newProgress.stages).forEach((key) => {
          if (newProgress.stages[key].status === "in_progress") {
            newProgress.stages[key].status = "completed";
            newProgress.stages[key].endTime = new Date();
          }
        });
      }

      if (data.status === SearchStages.FAILED) {
        const failedStage = newProgress.currentStage;
        newProgress.isSearching = false;
        newProgress.endTime = new Date();
        newProgress.currentStage = SearchStages.FAILED;
        if (failedStage && newProgress.stages[failedStage]) {
          newProgress.stages[failedStage].status = "failed";
          newProgress.stages[failedStage].error = data.message;
          newProgress.stages[failedStage].endTime = new Date();
        }
      }

      const stageCount = Object.keys(newProgress.stages).length;
      const completedCount = Object.values(newProgress.stages).filter(
        (s) => s.status === "completed",
      ).length;
      newProgress.overallProgress = (completedCount / stageCount) * 100;

      return newProgress;
    });
  }, []);

  // Handle progress update
  const handleProgressUpdate = useCallback((data) => {
    setProgress((prev) => {
      const newProgress = { ...prev };
      const payload = data?.payload || data;
      const stage = payload?.stage;

      if (stage && newProgress.stages[stage]) {
        const now = new Date();
        if (
          newProgress.currentStage &&
          newProgress.currentStage !== stage &&
          newProgress.stages[newProgress.currentStage] &&
          newProgress.stages[newProgress.currentStage].status === "in_progress"
        ) {
          newProgress.stages[newProgress.currentStage] = {
            ...newProgress.stages[newProgress.currentStage],
            status: "completed",
            endTime: now,
          };
        }

        newProgress.currentStage = stage;
        newProgress.isSearching = true;
        newProgress.stages[stage] = {
          ...newProgress.stages[stage],
          status: "in_progress",
          startTime: newProgress.stages[stage].startTime || now,
          progress: {
            current: payload.current,
            total: payload.total,
            percentage: payload.percentage,
          },
        };
      }

      const stageCount = Object.keys(newProgress.stages).length;
      const completedCount = Object.values(newProgress.stages).filter(
        (s) => s.status === "completed",
      ).length;
      newProgress.overallProgress = (completedCount / stageCount) * 100;

      return newProgress;
    });
  }, []);

  // Handle intermediate results
  const handleIntermediateResult = useCallback((data) => {
    setProgress((prev) => {
      const newProgress = { ...prev };
      const payload = data?.payload || data;
      const now = new Date();

      if (payload?.stage && newProgress.stages[payload.stage]) {
        const stageName = payload.stage;
        newProgress.stages[payload.stage] = {
          ...newProgress.stages[payload.stage],
          data: payload,
        };

        // Treat intermediate payload as "stage output is ready". This allows
        // core progress to finish before optional overview streaming.
        if (newProgress.stages[stageName].status === "in_progress") {
          newProgress.stages[stageName] = {
            ...newProgress.stages[stageName],
            status: "completed",
            endTime: now,
          };
        }

        const stageCount = Object.keys(newProgress.stages).length;
        const completedCount = Object.values(newProgress.stages).filter(
          (s) => s.status === "completed",
        ).length;
        newProgress.overallProgress = (completedCount / stageCount) * 100;

        if (
          stageName === FINAL_PROGRESS_STAGE &&
          completedCount === stageCount
        ) {
          newProgress.currentStage = SearchStages.COMPLETED;
          newProgress.isSearching = false;
          newProgress.endTime = newProgress.endTime || now;
        }
      }

      return newProgress;
    });
  }, []);

  // Handle completion
  const handleComplete = useCallback((data) => {
    const now = new Date();
    setProgress((prev) => ({
      ...prev,
      isSearching: false,
      endTime: now,
      currentStage: SearchStages.COMPLETED,
      overallProgress: 100,
      stages: PROGRESS_TRACKED_STAGES.reduce((acc, stageName) => {
        acc[stageName] = {
          ...prev.stages[stageName],
          status: "completed",
          endTime: prev.stages[stageName]?.endTime || now,
        };
        return acc;
      }, {}),
    }));
  }, []);

  // Handle error
  const handleError = useCallback((data) => {
    setProgress((prev) => {
      const newProgress = {
        ...prev,
        isSearching: false,
        endTime: new Date(),
        currentStage: SearchStages.FAILED,
      };

      // Mark the current stage as failed
      if (prev.currentStage && newProgress.stages[prev.currentStage]) {
        newProgress.stages[prev.currentStage] = {
          ...newProgress.stages[prev.currentStage],
          status: "failed",
          error: data.message || "An error occurred",
          endTime: new Date(),
        };
      }

      return newProgress;
    });
  }, []);

  // Get progress for a specific stage
  const getStageProgress = useCallback((stage) => {
    return progressRef.current.stages[stage] || createInitialStageState();
  }, []);

  // Get completed stages count
  const getCompletedStagesCount = useCallback(() => {
    return Object.values(progressRef.current.stages).filter(
      (s) => s.status === "completed",
    ).length;
  }, []);

  return {
    progress,
    resetProgress,
    startStage,
    startStageWithCompletedHistory,
    handleStatusUpdate,
    handleProgressUpdate,
    handleIntermediateResult,
    handleComplete,
    handleError,
    getStageProgress,
    getCompletedStagesCount,
    SearchStages,
  };
};

export default useSearchProgress;
